import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepProps } from "~/components/organisms/step";
import { chargepointDefaultValues } from "~/schemas/forms/chargepoint";
import {
  fetchChargepointDetails,
  formatChargepointData,
  initIndexState,
  initNumConnectorsState,
  isDuplicateChargepoint,
  updateChargepointValues,
} from "~/utils/chargepoint";
import { tryCatch } from "~/utils/try-catch";
import { Actions } from "./actions";
import { Search } from "./search";
import { Selected } from "./selected";

type ChargepointProps = {
  setValue: StepProps["setValue"];
  getValues: StepProps["getValues"];
  reset: StepProps["reset"];
};

export function Chargepoint({ setValue, getValues, reset }: ChargepointProps) {
  const { t } = useTranslation();
  const [serialSearch, setSerialSearch] = useState("");
  const [errorState, setErrorState] = useState({
    error: true,
    guidanceMessage: "Please add a chargepoint",
  });
  const [state, setState] = useState(() => ({
    numConnectors: initNumConnectorsState(),
    index: initIndexState(),
  }));
  const [isLoading, setIsLoading] = useState(false);

  async function addChargepoint() {
    if (!serialSearch) {
      setErrorState({
        error: true,
        guidanceMessage: t("Please enter a valid serial number"),
      });
      return;
    }

    const result = await tryCatch(fetchChargepointDetails(serialSearch));

    if (result.error) {
      setErrorState({
        error: true,
        guidanceMessage: t("Something went wrong, please try again"),
      });
      return;
    }

    if (!result.data) {
      setErrorState({
        error: true,
        guidanceMessage: t("Serial number does not exist"),
      });
      setIsLoading(false);
      return;
    }

    if (
      isDuplicateChargepoint(
        getValues("chargepoint"),
        result.data.chargePointIdentifier
      )
    ) {
      setErrorState({
        error: true,
        guidanceMessage: t("Please enter a different serial number"),
      });
      setIsLoading(false);
      return;
    }

    const formattedData = formatChargepointData(result.data);

    if (!formattedData) {
      setErrorState({
        error: true,
        guidanceMessage: t("Something went wrong, please try again"),
      });
      setIsLoading(false);
      return;
    }

    updateChargepointValues(formattedData, state.index, setValue);
    incrementState(formattedData.connectors);
    setErrorState({ error: false, guidanceMessage: "" });
    setSerialSearch("");
    setIsLoading(false);
  }

  function incrementState(numConnectors: number) {
    setState((prevState) => ({
      index: prevState.index + 1,
      numConnectors: prevState.numConnectors + Number(numConnectors),
    }));
  }

  function removeChargepoint(serialNumber: string) {
    const chargepoints = getValues("chargepoint");
    const removedChargepoint = chargepoints.find(
      (cp) => cp.serialNumber === serialNumber
    );

    if (!removedChargepoint) {
      return;
    }

    const updatedChargepoints = chargepoints.filter(
      (cp) => cp.serialNumber !== serialNumber
    );

    if (!updatedChargepoints.length) {
      reset({ chargepoint: [chargepointDefaultValues] });
      setState({ index: 0, numConnectors: 0 });
      return;
    }

    const numConnectorsToRemove = removedChargepoint.connectors;

    reset({ chargepoint: updatedChargepoints });
    setState((prevState) => ({
      index: prevState.index - 1,
      numConnectors: prevState.numConnectors - numConnectorsToRemove,
    }));
  }

  return (
    <>
      <Search
        search={serialSearch}
        setSearch={setSerialSearch}
        error={errorState.error}
        guidanceMessage={errorState.guidanceMessage}
      />
      <Actions
        isLoading={isLoading}
        addChargepoint={addChargepoint}
        dialogDescription={
          <>
            {state.index} {t("Chargepoints")}
            <br />
            {state.numConnectors} {t("Connectors")}
          </>
        }
      />
      <Selected
        removeChargepoint={removeChargepoint}
        getValues={getValues}
        setValue={setValue}
      />
    </>
  );
}
